import React, { useState, useEffect } from 'react';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { useUserRole } from '../../../hooks/useUserRole';
import { getAvatarById, getAvatarsByType } from '../../../utils/avatar.utils';
import avatarDefault from '../../../assets/images/avatars/avatar_default.png';
import MessageModal from './MessageModal';
import AvatarTooltip from './AvatarTooltip';
import { ROLES } from '@shared/types';

import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Avatar,
  TextField,
  ClickAwayListener,
  Backdrop,
  Portal,
} from '@mui/material';
interface MessageModalState {
  isOpen: boolean;
  type: 'success' | 'error';
  title: string;
  message: string;
}

export interface ProfileModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const userStore = useStoreByName('userStore');
  const { userData, getUserData } = userStore;

  const userRoleId = userData?.personalInfo?.role?.id;
  const userRole = useUserRole(userRoleId);
  const GUEST_USER_ID = 544;
  const isGuestUser = userData?.personalInfo?.id === GUEST_USER_ID;

  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [displayName, setDisplayName] = useState(userData?.personalInfo?.name || '');
  const [tempAvatarId, setTempAvatarId] = useState<number | null>(null);
  const [isAvatarTooltipOpen, setIsAvatarTooltipOpen] = useState(false);
  const [messageModal, setMessageModal] = useState<MessageModalState>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  useEffect(() => {
    if (isOpen) {
      getUserData();
    }
  }, [isOpen, getUserData]);

  useEffect(() => {
    setDisplayName(userData?.personalInfo?.name || '');
  }, [userData]);

  // פונקציה לתרגום התפקיד
  const getRoleTranslation = (roleId: number | undefined): string => {
    switch (roleId) {
      case ROLES.ADMIN:
        return t('roleAdmin');
      case ROLES.PRINCIPAL:
        return t('rolePrincipal');
      case ROLES.TEACHER:
        return t('roleTeacher');
      case ROLES.STUDENT:
        return t('roleStudent');
      default:
        return t('unknownRole');
    }
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
    setTempName(displayName);
  };

  const handleNameSave = async () => {
    try {
      await userStore.updateUserProfile({ name: tempName });
      setDisplayName(tempName);
      setIsEditingName(false);
      getUserData();
      setMessageModal({
        isOpen: true,
        type: 'success',
        title: t('success'),
        message: t('profileUpdatedSuccessfully') || 'הפרופיל עודכן בהצלחה',
      });
    } catch (error) {
      setMessageModal({
        isOpen: true,
        type: 'error',
        title: t('error'),
        message: t('profileUpdateFailed') || 'אירעה שגיאה בעדכון הפרופיל',
      });
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setTempName('');
  };

  const handleAvatarSelect = (id: number | null) => {
    setTempAvatarId(id);
  };

  const handleAvatarConfirm = async () => {
    if (tempAvatarId === null) return;
    try {
      await userStore.updateUserProfile({ avatarId: tempAvatarId });
      setIsAvatarTooltipOpen(false);
      setTempAvatarId(null);
      getUserData();
      setMessageModal({
        isOpen: true,
        type: 'success',
        title: t('success'),
        message: t('profileUpdatedSuccessfully') || 'הפרופיל עודכן בהצלחה!',
      });
    } catch (error) {
      setMessageModal({
        isOpen: true,
        type: 'error',
        title: t('error'),
        message: t('profileUpdateFailed') || 'אירעה שגיאה בעדכון הפרופיל!',
      });
    }
  };

  const handleAvatarCancel = () => {
    setTempAvatarId(null);
    setIsAvatarTooltipOpen(false);
  };

  const handleCloseModal = (event: any, reason: string) => {
    // מונע סגירת הדיאלוג בלחיצה על הרקע או מקש Escape
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose?.();
  };

  // אם משתמש אורח, הצג הודעה במקום המודל הרגיל
  if (isGuestUser && isOpen) {
    return (
      <MessageModal
        isOpen={isOpen}
        onClose={onClose}
        title={t('guestUserProfileTitle')}
        message={t('guestUserProfileMessage')}
        type="info"
      />
    );
  }

  return (
    <>
      {/* Backdrop מחוץ ל-Dialog כדי שיכסה את כל המסך */}
      <Portal>
        <Backdrop
          open={isAvatarTooltipOpen}
          onClick={handleAvatarCancel}
          sx={{
            zIndex: 1301,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        />
      </Portal>
      <Dialog 
        open={isOpen} 
        onClose={handleCloseModal} 
        maxWidth="xs" 
        fullWidth 
        className="profile-modal"
      >
        <IconButton
          onClick={onClose}
          aria-label="סגור"
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            zIndex: 3,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>{t('personalDetails')}</DialogTitle>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              className='profile-avatar'
              sx={{ width: 80, height: 80, marginBottom: '-10px'}}
              src={(() => {
                const avatarId = tempAvatarId !== null ? tempAvatarId : userData?.personalInfo?.avatar_id;
                const src = avatarId && getAvatarById(avatarId, userRole.avatarType);
                return typeof src === 'string' && src.length > 0 ? src : avatarDefault;
              })()}
            />
            <IconButton
              onClick={() => setIsAvatarTooltipOpen((prev) => !prev)}
              aria-label="שנה תמונת פרופיל"
              sx={{
                width: 26,
                height: 26,
                position: 'absolute',
                bottom: '-6px',
                right: '-6px',
                backgroundColor: 'rgba(214, 214, 214, 0.77)',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'white',
                },
              }}
              size="small"
            >
              <EditIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </Box>
        <Portal>
          {isAvatarTooltipOpen && (
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1302,
                maxWidth: '90vw',
                '@media (min-width: 480px)': {
                  maxWidth: '550px',
                },
                '@media (min-width: 768px)': {
                  maxWidth: '600px',
                },
                '@media (min-width: 1440px)': {
                  maxWidth: '650px',
                },
              }}
            >
              <ClickAwayListener onClickAway={handleAvatarCancel}>
                <Box>
                  <AvatarTooltip
                    avatars={getAvatarsByType(userRole.avatarType)}
                    onSelect={handleAvatarSelect}
                    onClose={handleAvatarCancel}
                    onConfirm={handleAvatarConfirm}
                    open={isAvatarTooltipOpen}
                  />
                </Box>
              </ClickAwayListener>
            </Box>
          )}
        </Portal>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditingName ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <TextField
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                />
                <IconButton onClick={handleNameSave} color="success" size="small" aria-label="שמור שם">
                  <CheckIcon />
                </IconButton>
                <IconButton onClick={handleNameCancel} size="small" aria-label="בטל עריכת שם">
                  <CloseIcon sx={{ fontSize: '18px' }} color="success" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>{t('name')}:</strong> {displayName}
                </Typography>
                <IconButton onClick={handleNameEdit} size="small" aria-label="ערוך שם">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          <Typography variant="body1" gutterBottom>
            <strong>{t('email')}:</strong> {userData?.personalInfo?.email}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>{t('userType')}:</strong> {getRoleTranslation(userData?.personalInfo?.role?.id)}
          </Typography>
          {userData?.personalInfo?.grades && userData.personalInfo.grades.length > 0 && (
            <Typography variant="body1" gutterBottom>
              <strong>{t('class')}:</strong> {userData.personalInfo.grades[0].name}
            </Typography>
          )}
          {userData?.personalInfo?.school && (
            <Typography variant="body1" gutterBottom>
              <strong>{t('school')}:</strong> {userData.personalInfo.school.name}
            </Typography>
          )}
          {userData?.personalInfo?.rank !== undefined && userData.personalInfo.rank !== null && (
            <Typography variant="body1" gutterBottom>
              <strong>{t('rank')}:</strong> {userData.personalInfo.rank}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', padding: '16px' }}>
        <Button 
          className="import-button circle" 
          onClick={onClose} 
          variant="contained"
          disabled={isAvatarTooltipOpen}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
      <MessageModal
        isOpen={messageModal.isOpen}
        onClose={() => setMessageModal((m) => ({ ...m, isOpen: false }))}
        type={messageModal.type}
        title={messageModal.message}
      />
    </Dialog>
    </>
  );
};

export default ProfileModal;
